import { useState } from 'react';
import { Link } from 'react-router-dom';
import { MessageSquare, Search, Loader2 } from 'lucide-react';
import { useConversations } from '../hooks/useApi';
import StatusBadge from '../components/StatusBadge';
import { formatRelativeTime, truncate } from '../lib/utils';

export default function ConversationsPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const { data, isLoading } = useConversations();

  const filteredConversations = data?.conversations.filter((conv) =>
    conv.applicant_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    conv.job_title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    conv.phone.includes(searchQuery)
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="relative w-full max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search conversations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input pl-10"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
        </div>
      ) : (
        <div className="card divide-y divide-gray-100">
          {filteredConversations?.map((conversation) => (
            <Link
              key={conversation.applicant_id}
              to={`/conversations/${conversation.applicant_id}`}
              className="flex items-center gap-4 p-4 hover:bg-gray-50 transition-colors"
            >
              {/* Avatar */}
              <div className="flex-shrink-0 w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
                <span className="text-lg font-medium text-primary-700">
                  {conversation.applicant_name.charAt(0)}
                </span>
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium text-gray-900 truncate">
                    {conversation.applicant_name}
                  </h3>
                  {conversation.last_message_at && (
                    <span className="text-xs text-gray-500">
                      {formatRelativeTime(conversation.last_message_at)}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  <StatusBadge status={conversation.status} className="text-xs" />
                  <span className="text-xs text-gray-500">• {conversation.job_title}</span>
                </div>
                {conversation.last_message && (
                  <p className="mt-1 text-sm text-gray-600 truncate">
                    {conversation.last_message_direction === 'outbound' && (
                      <span className="text-gray-400 mr-1">You:</span>
                    )}
                    {truncate(conversation.last_message, 60)}
                  </p>
                )}
              </div>

              {/* Unread badge */}
              {conversation.unread_count > 0 && (
                <div className="flex-shrink-0 w-6 h-6 bg-primary-600 rounded-full flex items-center justify-center">
                  <span className="text-xs font-medium text-white">
                    {conversation.unread_count}
                  </span>
                </div>
              )}

              {/* No messages indicator */}
              {!conversation.last_message && (
                <div className="flex-shrink-0 flex items-center text-gray-400">
                  <MessageSquare className="w-5 h-5" />
                </div>
              )}
            </Link>
          ))}

          {filteredConversations?.length === 0 && (
            <div className="py-12 text-center">
              <MessageSquare className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">No conversations found</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
